import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Button, I } from '@leanin/ui';
import { FlowShell } from '@/components/onboarding/FlowShell';
import { ChecklistColumn } from '@/components/onboarding/ChecklistColumn';
import { clearStoredPersona } from '@/components/onboarding/persona';
import { clearPendingInviteToken } from '@/components/onboarding/pending-invite';
import { useOnboardingProgress } from '@/components/onboarding/progress';
import { useCompleteOnboarding } from '@/lib/mutations/identity';
import { NL_ROLE, NL_STEPS } from './steps';
import {
  STEP_CONTENT_BY_KEY,
  patchChecklistHref,
} from '@/components/onboarding/checklist-registry';
import { PERSONA_STEP_KEYS } from '@/components/onboarding/persona-steps';

export const Route = createFileRoute('/onboarding/nl/complete')({
  component: NLOnboardingCompletePage,
});

function NLOnboardingCompletePage() {
  const navigate = useNavigate();
  const completeOnboarding = useCompleteOnboarding();
  const { data: progress } = useOnboardingProgress();

  const networkChecklist = PERSONA_STEP_KEYS['network-leader'].map((key) => {
    const content = STEP_CONTENT_BY_KEY[key];
    const href = progress
      ? patchChecklistHref(key, content.href, progress)
      : content.href;
    return { title: content.title, sub: content.sub, cta: content.cta, href };
  });

  const finish = async (to: string = '/feed') => {
    try {
      await completeOnboarding.mutateAsync({ persona: 'nl' });
    } catch {
      // Swallow — clicking "Finish onboarding" shouldn't be blocked by a
      // transient flag-write failure. If it persists the user will
      // simply see onboarding again next sign-in.
    }
    clearStoredPersona();
    clearPendingInviteToken();
    navigate({ to: to as '/' });
  };

  return (
    <FlowShell
      role={NL_ROLE}
      stepIndex={5}
      steps={NL_STEPS}
      title="Explore your community."
      primary={
        <Button variant="primary" icon={I.arrow} onClick={() => void finish()}>
          Finish onboarding
        </Button>
      }
      secondary={
        <Button
          variant="ghost"
          onClick={() => navigate({ to: '/onboarding/nl/welcome' })}
        >
          Return to welcome message
        </Button>
      }
    >
      <ChecklistColumn
        eyebrow="Next steps"
        title="Your Network Leader Checklist"
        items={networkChecklist}
        onNavigate={(href) => void finish(href)}
      />
    </FlowShell>
  );
}
